import { useState } from "react";

interface DownloadParams {
  data?: unknown;
  fileName?: string;
}

interface UseDownloadCsvReturn {
  isDownloading: boolean;
  downloadCsv: (params?: DownloadParams) => Promise<void>;
}

/**
 * Convert an array of objects to CSV string format
 * @param data Array of objects with string, number, or boolean values
 * @returns CSV formatted string
 */
export const convertToCsv = (data: Array<Record<string, string | number | boolean>>): string => {
  if (data.length === 0) return "";
  
  const headers = Object.keys(data[0]);
  const headerRow = headers.map((h) => `"${h}"`).join(",");
  
  const rows = data.map((row) =>
    headers.map((header) => {
      const value = row[header];
      const stringValue = String(value ?? "");
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      return stringValue.includes(",") || stringValue.includes("\n") || stringValue.includes('"')
        ? `"${stringValue.replace(/"/g, '""')}"`
        : stringValue;
    }).join(",")
  );
  
  return [headerRow, ...rows].join("\n");
};

/**
 * Reusable hook for downloading CSV files
 * Supports two modes: API call mode or direct data mode
 *
 * @example API Call Mode
 * const { isDownloading, downloadCsv } = useDownloadCsv(
 *   (payload) => apiClient.attendance.downloadCsv(payload.serviceId, payload.start, payload.end)
 * );
 *
 * await downloadCsv({
 *   data: { serviceId: '123', start: dayjs(), end: dayjs() },
 *   fileName: 'report.csv'
 * });
 *
 * @example Direct Data Mode
 * const { isDownloading, downloadCsv } = useDownloadCsv(blobData, 'report.csv');
 * await downloadCsv({ fileName: 'custom-name.csv' }); // fileName is optional
 */
export function useDownloadCsv(
  apiCall: (payload: unknown) => Promise<{ data: string | Blob; fileName?: string }>
): UseDownloadCsvReturn;

export function useDownloadCsv(
  data: string | Blob | unknown,
  fileName?: string
): UseDownloadCsvReturn;

export function useDownloadCsv(
  apiCallOrData: ((payload: unknown) => Promise<{ data: string | Blob; fileName?: string }>) | string | Blob | unknown,
  initialFileName?: string
): UseDownloadCsvReturn {
  const [isDownloading, setIsDownloading] = useState(false);
  const isApiCall = typeof apiCallOrData === "function";

  const downloadCsv = async (params?: DownloadParams) => {
    setIsDownloading(true);
    try {
      let response: { data: string | Blob; fileName?: string };

      if (isApiCall) {
        // API call mode
        response = await (apiCallOrData as (payload: unknown) => Promise<{ data: string | Blob; fileName?: string }>)(
          params?.data
        );
      } else {
        // Direct data mode
        response = {
          data: apiCallOrData as string | Blob,
          fileName: params?.fileName || initialFileName,
        };
      }

      // Create blob from response data
      const blob = new Blob([response.data], { type: "text/csv" });

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = params?.fileName || response.fileName || "download.csv";

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download CSV:", error);
      throw error;
    } finally {
      setIsDownloading(false);
    }
  };

  return { isDownloading, downloadCsv };
}
